import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search,
    Plus,
    ArrowRight,
    Layers,
    Home,
    Package,
    Sparkles,
    Trash2,
    ChevronDown
} from 'lucide-react';
import { Button, toast, ConfirmationModal, LoadingState, ErrorState, EmptyState } from '../../components/v2/V2Components';
import RoomService from '../../services/RoomService';
import HouseService from '../../services/HouseService';
import { getRoomImageUrl, PLACEHOLDER_IMAGES } from '../../constants/images';
import usePageView from '../../hooks/usePageView';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents';

const PAGE_SIZE = 20;
const SORT_OPTIONS = [
    { value: 'recently_updated', label: 'Recently updated' },
    { value: 'name', label: 'Name A–Z' },
    { value: 'oldest', label: 'Oldest' }
];

const Spaces = () => {
    usePageView(ANALYTICS_EVENTS.SPACES_VIEWED);
    const navigate = useNavigate();
    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sort, setSort] = useState('recently_updated');
    const [houses, setHouses] = useState([]);
    const [houseFilter, setHouseFilter] = useState('');
    const [pagination, setPagination] = useState({ total: 0, limit: PAGE_SIZE, offset: 0, has_more: false });
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    const [deletingRoom, setDeletingRoom] = useState(false);

    const handleDeleteRoom = async () => {
        if (!deleteCandidate) return;
        setDeletingRoom(true);
        try {
            await RoomService.deleteRoom(deleteCandidate.id);
            setRooms(prev => prev.filter(r => r.id !== deleteCandidate.id));
            setPagination(prev => ({
                ...prev,
                total: Math.max(0, prev.total - 1)
            }));
            toast.success('Space deleted successfully');
        } catch (err) {
            toast.error(err.message || 'Failed to delete space');
        } finally {
            setDeletingRoom(false);
            setDeleteCandidate(null);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            setError(null);
            try {
                const { rooms: fetched, pagination: meta } = await RoomService.getRooms(buildParams(0));
                setRooms(fetched || []);
                setPagination(meta || { total: 0, limit: PAGE_SIZE, offset: 0, has_more: false });
            } catch (err) {
                console.error('Failed to fetch rooms:', err);
                setError(err.message || 'Failed to load spaces');
            }
            setLoading(false);
        };
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, sort, houseFilter]);

    // Houses load once, in their own effect: they populate the filter dropdown
    // and the id→name map for the card chips, and must not be refetched every
    // time the filter or sort changes.
    useEffect(() => {
        let cancelled = false;
        HouseService.getHouses()
            .then(list => { if (!cancelled) setHouses(Array.isArray(list) ? list : []); })
            .catch(err => console.error('Failed to fetch houses:', err));
        return () => { cancelled = true; };
    }, []);

    // Rooms carry house_id but no house name, so resolve it client-side.
    const houseNameById = useMemo(
        () => Object.fromEntries(houses.map(h => [h.id, h.name])),
        [houses]
    );

    const buildParams = (offset) => {
        const params = { limit: PAGE_SIZE, offset, sort };
        if (debouncedSearch) params.search = debouncedSearch;
        if (houseFilter) params.house_id = Number(houseFilter);
        return params;
    };

    const fetchRooms = async () => {
        try {
            setLoading(true);
            setError(null);
            const { rooms: fetched, pagination: meta } = await RoomService.getRooms(buildParams(0));
            setRooms(fetched || []);
            setPagination(meta || { total: 0, limit: PAGE_SIZE, offset: 0, has_more: false });
        } catch (err) {
            console.error('Failed to fetch rooms:', err);
            // Without this, Retry that fails again shows nothing, and a
            // successful Retry leaves the stale error banner up forever.
            setError(err.message || 'Failed to load spaces');
        } finally {
            setLoading(false);
        }
    };

    const loadMoreRooms = async () => {
        if (loadingMore || !pagination.has_more) return;
        try {
            setLoadingMore(true);
            const { rooms: fetched, pagination: meta } = await RoomService.getRooms(buildParams(rooms.length));
            setRooms(prev => [...prev, ...(fetched || [])]);
            setPagination(meta || pagination);
        } catch (err) {
            console.error('Failed to load more rooms:', err);
            toast.error('Failed to load more spaces');
        } finally {
            setLoadingMore(false);
        }
    };

    /* Stats below are summed from the loaded page only, so they are only
       displayed when the full dataset has been fetched (no more pages). */
    const allRoomsLoaded = !pagination.has_more;
    // Any active narrowing — search OR house — means an empty result is a
    // filtered miss, not an empty account, so the CTA must be suppressed.
    const isFiltered = Boolean(debouncedSearch || houseFilter);
    const totalItems = rooms.reduce((sum, r) => sum + (r.items_count || 0), 0);
    const uniqueFloors = [...new Set(rooms.map(r => r.floor).filter(Boolean))].length;

    /* Map floor names to badge styles */
    const getFloorBadgeStyle = (floor) => {
        if (!floor) return 'bg-[color-mix(in_srgb,var(--primary),transparent_20%)] text-white';
        const f = floor.toLowerCase();
        if (f.includes('basement') || f.includes('lower')) return 'bg-[color-mix(in_srgb,var(--primary-container),transparent_20%)] text-[var(--primary-container-text)]';
        if (f.includes('2nd') || f.includes('upper') || f.includes('attic')) return 'bg-[color-mix(in_srgb,var(--primary-container),transparent_20%)] text-[var(--primary-container-text)]';
        return 'bg-[color-mix(in_srgb,var(--primary),transparent_20%)] text-white';
    };

    if (loading) {
        return <LoadingState />;
    }

    // A failed load with nothing to show gets a full error state with Retry;
    // the inline banner below is reserved for failures when rooms are
    // already on screen (so existing content isn't blanked).
    if (error && rooms.length === 0) {
        return (
            <ErrorState
                title="Error Loading Rooms"
                message={error}
                onRetry={fetchRooms}
            />
        );
    }

    return (
        <div className="space-y-10 animate-fade-in">
            {/* Editorial Header */}
            <header className="flex flex-col md:flex-row justify-between items-end gap-6">
                <div className="max-w-2xl">
                    <h1 className="font-headline text-4xl md:text-5xl font-extrabold tracking-tight text-[var(--primary)] mb-3">
                        Your Rooms
                    </h1>
                    <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">
                        Organize your living space with architectural precision. Monitor inventory and maintenance schedules for each zone of The House.
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Search */}
                    <div className="bg-[var(--bg-card)] rounded-xl flex items-center px-4 py-3 gap-2 shadow-[var(--shadow-sm)]">
                        <Search size={16} className="text-[var(--text-muted)]" />
                        <input
                            placeholder="Search rooms..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="text-sm outline-none w-32 md:w-48 bg-transparent text-[var(--text-main)] placeholder-[var(--text-muted)] font-body"
                        />
                    </div>
                    {/* House filter — only meaningful with more than one house.
                        Carries both name and data-testid so the E2E page object's
                        `select[name*="house"], [data-testid="house-filter"]`
                        locator resolves unambiguously. */}
                    {houses.length > 1 && (
                        <div className="relative bg-[var(--bg-card)] rounded-xl shadow-[var(--shadow-sm)]">
                            <select
                                name="house_id"
                                data-testid="house-filter"
                                value={houseFilter}
                                onChange={(e) => setHouseFilter(e.target.value)}
                                aria-label="Filter rooms by house"
                                className="appearance-none text-sm bg-transparent text-[var(--text-main)] font-body px-4 py-3 pr-9 outline-none cursor-pointer"
                            >
                                <option value="">All houses</option>
                                {houses.map(h => (
                                    <option key={h.id} value={h.id}>{h.name}</option>
                                ))}
                            </select>
                            <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                        </div>
                    )}
                    {/* Sort */}
                    <div className="relative bg-[var(--bg-card)] rounded-xl shadow-[var(--shadow-sm)]">
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            aria-label="Sort rooms"
                            className="appearance-none text-sm bg-transparent text-[var(--text-main)] font-body px-4 py-3 pr-9 outline-none cursor-pointer"
                        >
                            {SORT_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    </div>
                    <Button
                        variant="primaryGradient"
                        leadingIcon={Plus}
                        onClick={() => navigate('/spaces/new')}
                    >
                        <span className="hidden md:inline">Add New Room</span>
                    </Button>
                </div>
            </header>

            {error && (
                <div className="p-4 bg-[var(--error-container)] text-[var(--error)] rounded-xl">
                    {error}
                    <button onClick={fetchRooms} className="ml-4 underline font-medium">Retry</button>
                </div>
            )}

            {/* Rooms Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
                {rooms.map((room) => (
                    <div
                        key={room.id}
                        data-testid="room-card"
                        onClick={() => navigate(`/room/${room.id}`)}
                        className="group bg-[var(--bg-card)] rounded-2xl overflow-hidden transition-all duration-500 hover:translate-y-[-4px] cursor-pointer shadow-[var(--shadow-sm)]"
                    >
                        {/* Room Image with Floor Badge */}
                        <div className="h-56 relative overflow-hidden bg-[var(--bg-hover)]">
                            <img
                                src={getRoomImageUrl(room)}
                                alt={room.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = PLACEHOLDER_IMAGES.room;
                                }}
                            />
                            {room.floor && (
                                <div className="absolute top-4 right-4">
                                    <span className={`${getFloorBadgeStyle(room.floor)} backdrop-blur-md text-xs px-3 py-1.5 rounded-full font-medium`}>
                                        {room.floor}
                                    </span>
                                </div>
                            )}
                            {/* House chip — opposite corner from the floor badge so
                                the two never collide. Hand-rolled rather than the
                                shared Badge, whose variant union is closed to
                                maintenance statuses and which force-uppercases
                                its label. Renders nothing for an unanchored room. */}
                            {houseNameById[room.house_id] && (
                                <div className="absolute top-4 left-4 max-w-[60%]">
                                    <span
                                        className="block truncate bg-[color-mix(in_srgb,var(--bg-card),transparent_15%)] text-[var(--text-secondary)] backdrop-blur-md text-xs px-3 py-1.5 rounded-full font-medium"
                                        title={houseNameById[room.house_id]}
                                    >
                                        {houseNameById[room.house_id]}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Room Info */}
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-5">
                                <div>
                                    <h3 className="font-headline text-xl font-bold text-[var(--primary)]">{room.name}</h3>
                                </div>
                                {/* Delete stays visible below md so touch devices
                                    (no hover) can reach it; hover-reveal from md up. */}
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setDeleteCandidate(room); }}
                                    className="p-1.5 text-[var(--error)] hover:bg-[var(--error-container)] rounded-lg md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 transition-all theme-transition"
                                    title="Delete Space"
                                    aria-label={`Delete ${room.name}`}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>

                            {/* Stat Grid */}
                            <div className="grid grid-cols-1 gap-3 mb-6">
                                <div className="bg-[var(--bg-hover)] p-3.5 rounded-xl">
                                    <Package size={16} className="text-[var(--primary)] mb-1.5" />
                                    <span className="text-[11px] text-[var(--text-secondary)] uppercase font-bold tracking-wider block">Inventory</span>
                                    <p className="text-base font-bold text-[var(--primary)]">
                                        {room.items_count !== undefined ? `${room.items_count} items` : '—'}
                                    </p>
                                </div>
                            </div>

                            {/* Footer Row */}
                            <div className="flex items-center justify-end pt-5">
                                <button className="text-[var(--primary)] font-bold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                                    View Details
                                    <ArrowRight size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}

                {/* "Expand The House" Add Card */}
                <button
                    onClick={() => navigate('/spaces/new')}
                    className="border-2 border-dashed border-[color-mix(in_srgb,var(--text-muted),transparent_70%)] rounded-2xl flex flex-col items-center justify-center p-12 group hover:border-[color-mix(in_srgb,var(--primary),transparent_50%)] transition-colors cursor-pointer min-h-[380px]"
                >
                    <div className="w-16 h-16 rounded-full bg-[var(--bg-elevated)] flex items-center justify-center mb-4 group-hover:bg-[var(--primary-bg)] transition-colors">
                        <Home size={28} className="text-[var(--text-muted)] group-hover:text-[var(--primary)] transition-colors" />
                    </div>
                    <h4 className="font-headline text-xl font-bold text-[var(--primary)]">Expand The House</h4>
                    <p className="text-[var(--text-secondary)] text-sm text-center mt-2 max-w-[220px]">
                        Add another room, garage, or outdoor living space to your management system.
                    </p>
                </button>
            </div>

            {/* Load More */}
            {pagination.has_more && (
                <div className="flex justify-center">
                    <button
                        onClick={loadMoreRooms}
                        disabled={loadingMore}
                        className="px-6 py-3 bg-[var(--bg-card)] text-[var(--primary)] rounded-xl font-semibold shadow-[var(--shadow-sm)] hover:shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {loadingMore ? 'Loading…' : `Load more (${pagination.total - rooms.length} remaining)`}
                    </button>
                </div>
            )}

            {/* Empty State — CTA only when truly empty, not for a filtered miss */}
            {rooms.length === 0 && !loading && (
                <EmptyState
                    icon={Layers}
                    title={isFiltered ? 'No rooms found' : 'No rooms yet'}
                    message={isFiltered
                        ? 'Try adjusting your search or house filter'
                        : 'Start building your digital house by adding your first room or space'}
                    actionLabel={isFiltered ? undefined : 'Add Your First Room'}
                    onAction={isFiltered ? undefined : () => navigate('/spaces/new')}
                />
            )}

            {/* Editorial Footer Stats (Asymmetric Layout) */}
            {pagination.total > 0 && (
                <section className="grid grid-cols-12 gap-6 mt-8">
                    {/* Hero Stat Card (dark) */}
                    <div className="col-span-12 lg:col-span-8 bg-[var(--primary-container)] p-10 rounded-3xl text-white">
                        <div className="flex flex-col md:flex-row justify-between gap-10">
                            <div>
                                <h2 className="font-headline text-2xl md:text-3xl font-bold mb-2">Total Managed Footprint</h2>
                                <p className="text-[var(--primary-container-text)] max-w-md text-sm leading-relaxed">
                                    Your digital house spans {pagination.total} room{pagination.total !== 1 ? 's' : ''}
                                    {allRoomsLoaded && ` across ${uniqueFloors || 1} floor${uniqueFloors !== 1 ? 's' : ''}`}.
                                    All spaces are being monitored for optimal living conditions.
                                </p>
                            </div>
                            <div className="flex gap-10">
                                <div className="text-center">
                                    <p className="text-4xl md:text-5xl font-black mb-1">{pagination.total}</p>
                                    <p className="text-[11px] uppercase tracking-widest text-[var(--primary-container-text)]">Active Zones</p>
                                </div>
                                {allRoomsLoaded && (
                                    <div className="text-center">
                                        <p className="text-4xl md:text-5xl font-black mb-1">{totalItems}</p>
                                        <p className="text-[11px] uppercase tracking-widest text-[var(--primary-container-text)]">Assets</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Tip Card (warm peach) */}
                    <div className="col-span-12 lg:col-span-4 h-full">
                        <div className="bg-[var(--tertiary-fixed)] h-full p-8 rounded-3xl flex flex-col justify-between min-h-[200px]">
                            <Sparkles size={32} className="text-[var(--primary)]" />
                            <div>
                                <h3 className="font-headline text-lg font-bold text-[var(--primary)] mt-4">Maintenance Tip</h3>
                                <p className="text-[var(--text-secondary)] mt-2 text-sm leading-relaxed">
                                    Regular walkthroughs of each room help catch small issues before they become costly repairs.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Delete Room Modal */}
            <ConfirmationModal
                isOpen={!!deleteCandidate}
                onClose={() => setDeleteCandidate(null)}
                onConfirm={handleDeleteRoom}
                isLoading={deletingRoom}
                title={`Delete ${deleteCandidate?.name || 'this space'}?`}
                message="Are you sure you want to delete this space? Any associated items and tasks will be unassigned but not deleted. This action cannot be undone."
                confirmText={deletingRoom ? 'Deleting...' : 'Delete'}
                isDanger={true}
                icon={Trash2}
            />
        </div>
    );
};

export default Spaces;
